import { Component, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PersonasAuthService } from '../services/personas-auth.service';
import { CoreButtonComponent } from '@shared/components/core-button/core-button.component';
import { CoreOtpComponent } from '@shared/components/core-otp/core-otp.component';
import { AppIcons } from '@core/utilities/icons';
import { AlertService } from '@shared/services/alert.service';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
    selector: 'app-verificacion-otp',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink, FontAwesomeModule, CoreButtonComponent, CoreOtpComponent],
    templateUrl: './verificacion-otp.component.html',
    styleUrl: './verificacion-otp.component.scss'
})
export class VerificacionOtpComponent implements OnInit, OnDestroy {
    readonly icons = AppIcons;

    @ViewChild(CoreOtpComponent) private otpField?: CoreOtpComponent;

    // Services
    private router = inject(Router);
    private alertService = inject(AlertService);
    public authService = inject(PersonasAuthService);

    // State matching ValidacionTelefono style
    otpValue: string = '';
    isOtpComplete: boolean = false;

    isLoading = false;

    // Timer State
    timeLeft = 60; // seconds
    private timerInterval: any;

    currentUser$ = this.authService.currentUser$;

    ngOnInit() {
        // Check user session
        if (!this.authService.getCurrentUser()) {
            this.router.navigateByUrl('/personas/ingreso');
            return;
        }
        this.startTimer();
    }

    ngOnDestroy() {
        this.stopTimer();
    }

    // --- OTP Logic matching ValidacionTelefono ---

    onOtpChange(otp: string) {
        this.otpValue = otp;
        this.isOtpComplete = otp.length === 6;
    }

    onOtpComplete(otp: string) {
        this.isOtpComplete = true;
        // Automatically submit or allow manual submit?
        // Reference component allows manual click on "Continuar"
    }

    // --- Timer Logic ---

    startTimer() {
        this.stopTimer();
        this.timeLeft = 60;
        this.timerInterval = setInterval(() => {
            if (this.timeLeft > 0) {
                this.timeLeft--;
            } else {
                this.stopTimer();
            }
        }, 1000);

        this.authService.requestOtp().subscribe();
    }

    stopTimer() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    }

    get formattedTime(): string {
        const m = Math.floor(this.timeLeft / 60);
        const s = this.timeLeft % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    }

    onResendCode() {
        if (this.timeLeft > 0) return;

        this.isLoading = true;
        this.authService.requestOtp().subscribe({
            next: () => {
                this.isLoading = false;
                this.startTimer();
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    // --- Submission ---

    onSubmit(event?: Event) {
        event?.preventDefault();

        if (!this.isOtpComplete || this.isLoading) return;

        this.isLoading = true;

        const invalidCodeMsg =
            'El código ingresado no es válido o ha expirado. Verifica e inténtalo de nuevo.';

        this.authService.verifyOtp(this.otpValue).pipe(
            tap(isValid => {
                this.isLoading = false;
                if (isValid) {
                    this.router.navigateByUrl('/personas/inicio');
                    return;
                }
                void this.alertService
                    .error('Código incorrecto', invalidCodeMsg)
                    .then(() => this.otpField?.clear());
            }),
            catchError((err: Error) => {
                this.isLoading = false;
                const msg =
                    err?.message || 'No pudimos verificar el código. Intenta de nuevo más tarde.';
                void this.alertService
                    .error('No pudimos verificar el código', msg)
                    .then(() => this.otpField?.clear());
                return of(false);
            })
        ).subscribe();
    }
}
